import { __assign } from "tslib";
import { GraphQLEnumType, isSchema, GraphQLScalarType, GraphQLUnionType, GraphQLInterfaceType, GraphQLObjectType, isSpecifiedScalarType, isScalarType, isEnumType, isUnionType, isInterfaceType, isObjectType, } from 'graphql';
import { mapSchema, MapperKind, forEachDefaultValue, serializeInputValue, healSchema, parseInputValue, forEachField, } from '@graphql-tools/utils';
import { checkForResolveTypeResolver } from './checkForResolveTypeResolver';
import { extendResolversFromInterfaces } from './extendResolversFromInterfaces';
export function addResolversToSchema(schemaOrOptions, legacyInputResolvers, legacyInputValidationOptions) {
    var options = isSchema(schemaOrOptions)
        ? {
            schema: schemaOrOptions,
            resolvers: legacyInputResolvers,
            resolverValidationOptions: legacyInputValidationOptions,
        }
        : schemaOrOptions;
    var schema = options.schema, inputResolvers = options.resolvers, defaultFieldResolver = options.defaultFieldResolver, _a = options.resolverValidationOptions, resolverValidationOptions = _a === void 0 ? {} : _a, _b = options.inheritResolversFromInterfaces, inheritResolversFromInterfaces = _b === void 0 ? false : _b, _c = options.updateResolversInPlace, updateResolversInPlace = _c === void 0 ? false : _c;
    var _d = resolverValidationOptions.requireResolversToMatchSchema, requireResolversToMatchSchema = _d === void 0 ? 'error' : _d, requireResolversForResolveType = resolverValidationOptions.requireResolversForResolveType;
    var resolvers = inheritResolversFromInterfaces
        ? extendResolversFromInterfaces(schema, inputResolvers)
        : inputResolvers;
    Object.keys(resolvers).forEach(function (typeName) {
        var resolverValue = resolvers[typeName];
        var resolverType = typeof resolverValue;
        if (typeName === '__schema') {
            if (resolverType !== 'function') {
                throw new Error("\"" + typeName + "\" defined in resolvers, but has invalid value \"" + resolverValue + "\". A schema resolver's value must be of type object or function.");
            }
        }
        else {
            if (resolverType !== 'object') {
                throw new Error("\"" + typeName + "\" defined in resolvers, but has invalid value \"" + resolverValue + "\". The resolver's value must be of type object.");
            }
            var type_1 = schema.getType(typeName);
            if (type_1 == null) {
                if (requireResolversToMatchSchema === 'ignore') {
                    return;
                }
                throw new Error("\"" + typeName + "\" defined in resolvers, but not in schema");
            }
            else if (isSpecifiedScalarType(type_1)) {
                // allow -- without recommending -- overriding of specified scalar types
                Object.keys(resolverValue).forEach(function (fieldName) {
                    if (fieldName.startsWith('__')) {
                        type_1[fieldName.substring(2)] = resolverValue[fieldName];
                    }
                    else {
                        type_1[fieldName] = resolverValue[fieldName];
                    }
                });
            }
            else if (isEnumType(type_1)) {
                var values_1 = type_1.getValues();
                Object.keys(resolverValue).forEach(function (fieldName) {
                    if (!fieldName.startsWith('__') &&
                        !values_1.some(function (value) { return value.name === fieldName; }) &&
                        requireResolversToMatchSchema &&
                        requireResolversToMatchSchema !== 'ignore') {
                        throw new Error(type_1.name + "." + fieldName + " was defined in resolvers, but not present within " + type_1.name);
                    }
                });
            }
            else if (isUnionType(type_1)) {
                Object.keys(resolverValue).forEach(function (fieldName) {
                    if (!fieldName.startsWith('__') &&
                        requireResolversToMatchSchema &&
                        requireResolversToMatchSchema !== 'ignore') {
                        throw new Error(type_1.name + "." + fieldName + " was defined in resolvers, but " + type_1.name + " is not an object or interface type");
                    }
                });
            }
            else if (isObjectType(type_1) || isInterfaceType(type_1)) {
                Object.keys(resolverValue).forEach(function (fieldName) {
                    if (!fieldName.startsWith('__')) {
                        var fields = type_1.getFields();
                        var field = fields[fieldName];
                        if (field == null && requireResolversToMatchSchema && requireResolversToMatchSchema !== 'ignore') {
                            throw new Error(typeName + "." + fieldName + " defined in resolvers, but not in schema");
                        }
                        var fieldResolve = resolverValue[fieldName];
                        if (typeof fieldResolve !== 'function' && typeof fieldResolve !== 'object') {
                            throw new Error("Resolver " + typeName + "." + fieldName + " must be object or function");
                        }
                    }
                });
            }
        }
    });
    schema = updateResolversInPlace
        ? addResolversToExistingSchema(schema, resolvers, defaultFieldResolver)
        : createNewSchemaWithResolvers(schema, resolvers, defaultFieldResolver);
    if (requireResolversForResolveType || requireResolversForResolveType !== 'ignore') {
        checkForResolveTypeResolver(schema, requireResolversForResolveType);
    }
    return schema;
}
function addResolversToExistingSchema(schema, resolvers, defaultFieldResolver) {
    var typeMap = schema.getTypeMap();
    Object.keys(resolvers).forEach(function (typeName) {
        if (typeName !== '__schema') {
            var type_2 = schema.getType(typeName);
            var resolverValue_1 = resolvers[typeName];
            if (isScalarType(type_2)) {
                Object.keys(resolverValue_1).forEach(function (fieldName) {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                    if (fieldName.startsWith('__')) {
                        type_2[fieldName.substring(2)] = resolverValue_1[fieldName];
                    }
                    else if (fieldName === 'astNode' && type_2.astNode != null) {
                        type_2.astNode = __assign(__assign({}, type_2.astNode), { description: (_c = (_b = (_a = resolverValue_1) === null || _a === void 0 ? void 0 : _a.astNode) === null || _b === void 0 ? void 0 : _b.description) !== null && _c !== void 0 ? _c : type_2.astNode.description, directives: ((_d = type_2.astNode.directives) !== null && _d !== void 0 ? _d : []).concat((_g = (_f = (_e = resolverValue_1) === null || _e === void 0 ? void 0 : _e.astNode) === null || _f === void 0 ? void 0 : _f.directives) !== null && _g !== void 0 ? _g : []) });
                    }
                    else if (fieldName === 'extensionASTNodes' && type_2.extensionASTNodes != null) {
                        type_2.extensionASTNodes = ((_h = []) !== null && _h !== void 0 ? _h : type_2.extensionASTNodes).concat((_k = (_j = resolverValue_1) === null || _j === void 0 ? void 0 : _j.extensionASTNodes) !== null && _k !== void 0 ? _k : []);
                    }
                    else if (fieldName === 'extensions' &&
                        type_2.extensions != null &&
                        resolverValue_1.extensions != null) {
                        type_2.extensions = Object.assign({}, type_2.extensions, resolverValue_1.extensions);
                    }
                    else {
                        type_2[fieldName] = resolverValue_1[fieldName];
                    }
                });
            }
            else if (isEnumType(type_2)) {
                var config_1 = type_2.toConfig();
                var enumValueConfigMap_1 = config_1.values;
                Object.keys(resolverValue_1).forEach(function (fieldName) {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                    if (fieldName.startsWith('__')) {
                        config_1[fieldName.substring(2)] = resolverValue_1[fieldName];
                    }
                    else if (fieldName === 'astNode' && config_1.astNode != null) {
                        config_1.astNode = __assign(__assign({}, config_1.astNode), { description: (_c = (_b = (_a = resolverValue_1) === null || _a === void 0 ? void 0 : _a.astNode) === null || _b === void 0 ? void 0 : _b.description) !== null && _c !== void 0 ? _c : config_1.astNode.description, directives: ((_d = config_1.astNode.directives) !== null && _d !== void 0 ? _d : []).concat((_g = (_f = (_e = resolverValue_1) === null || _e === void 0 ? void 0 : _e.astNode) === null || _f === void 0 ? void 0 : _f.directives) !== null && _g !== void 0 ? _g : []) });
                    }
                    else if (fieldName === 'extensionASTNodes' && config_1.extensionASTNodes != null) {
                        config_1.extensionASTNodes = config_1.extensionASTNodes.concat((_j = (_h = resolverValue_1) === null || _h === void 0 ? void 0 : _h.extensionASTNodes) !== null && _j !== void 0 ? _j : []);
                    }
                    else if (fieldName === 'extensions' &&
                        type_2.extensions != null &&
                        resolverValue_1.extensions != null) {
                        type_2.extensions = Object.assign({}, type_2.extensions, resolverValue_1.extensions);
                    }
                    else if (enumValueConfigMap_1[fieldName]) {
                        enumValueConfigMap_1[fieldName].value = resolverValue_1[fieldName];
                    }
                });
                typeMap[typeName] = new GraphQLEnumType(config_1);
            }
            else if (isUnionType(type_2)) {
                Object.keys(resolverValue_1).forEach(function (fieldName) {
                    if (fieldName.startsWith('__')) {
                        type_2[fieldName.substring(2)] = resolverValue_1[fieldName];
                    }
                });
            }
            else if (isObjectType(type_2) || isInterfaceType(type_2)) {
                Object.keys(resolverValue_1).forEach(function (fieldName) {
                    if (fieldName.startsWith('__')) {
                        // this is for isTypeOf and resolveType and all the other stuff.
                        type_2[fieldName.substring(2)] = resolverValue_1[fieldName];
                        return;
                    }
                    var fields = type_2.getFields();
                    var field = fields[fieldName];
                    if (field != null) {
                        var fieldResolve = resolverValue_1[fieldName];
                        if (typeof fieldResolve === 'function') {
                            // for convenience. Allows shorter syntax in resolver definition file
                            field.resolve = fieldResolve;
                        }
                        else {
                            setFieldProperties(field, fieldResolve);
                        }
                    }
                });
            }
        }
    });
    // serialize all default values prior to healing fields with new scalar/enum types.
    forEachDefaultValue(schema, serializeInputValue);
    // schema may have new scalar/enum types that require healing
    healSchema(schema);
    // reparse all default values with new parsing functions.
    forEachDefaultValue(schema, parseInputValue);
    if (defaultFieldResolver != null) {
        forEachField(schema, function (field) {
            if (!field.resolve) {
                field.resolve = defaultFieldResolver;
            }
        });
    }
    return schema;
}
function createNewSchemaWithResolvers(schema, resolvers, defaultFieldResolver) {
    var _a, _b;
    schema = mapSchema(schema, (_a = {},
        _a[MapperKind.SCALAR_TYPE] = function (type) {
            var config = type.toConfig();
            var resolverValue = resolvers[type.name];
            if (!isSpecifiedScalarType(type) && resolverValue != null) {
                Object.keys(resolverValue).forEach(function (fieldName) {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                    if (fieldName.startsWith('__')) {
                        config[fieldName.substring(2)] = resolverValue[fieldName];
                    }
                    else if (fieldName === 'astNode' && config.astNode != null) {
                        config.astNode = __assign(__assign({}, config.astNode), { description: (_c = (_b = (_a = resolverValue) === null || _a === void 0 ? void 0 : _a.astNode) === null || _b === void 0 ? void 0 : _b.description) !== null && _c !== void 0 ? _c : config.astNode.description, directives: ((_d = config.astNode.directives) !== null && _d !== void 0 ? _d : []).concat((_g = (_f = (_e = resolverValue) === null || _e === void 0 ? void 0 : _e.astNode) === null || _f === void 0 ? void 0 : _f.directives) !== null && _g !== void 0 ? _g : []) });
                    }
                    else if (fieldName === 'extensionASTNodes' && config.extensionASTNodes != null) {
                        config.extensionASTNodes = config.extensionASTNodes.concat((_j = (_h = resolverValue) === null || _h === void 0 ? void 0 : _h.extensionASTNodes) !== null && _j !== void 0 ? _j : []);
                    }
                    else if (fieldName === 'extensions' &&
                        config.extensions != null &&
                        resolverValue.extensions != null) {
                        config.extensions = Object.assign({}, type.extensions, resolverValue.extensions);
                    }
                    else {
                        config[fieldName] = resolverValue[fieldName];
                    }
                });
                return new GraphQLScalarType(config);
            }
        },
        _a[MapperKind.ENUM_TYPE] = function (type) {
            var resolverValue = resolvers[type.name];
            var config = type.toConfig();
            var enumValueConfigMap = config.values;
            if (resolverValue != null) {
                Object.keys(resolverValue).forEach(function (fieldName) {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                    if (fieldName.startsWith('__')) {
                        config[fieldName.substring(2)] = resolverValue[fieldName];
                    }
                    else if (fieldName === 'astNode' && config.astNode != null) {
                        config.astNode = __assign(__assign({}, config.astNode), { description: (_c = (_b = (_a = resolverValue) === null || _a === void 0 ? void 0 : _a.astNode) === null || _b === void 0 ? void 0 : _b.description) !== null && _c !== void 0 ? _c : config.astNode.description, directives: ((_d = config.astNode.directives) !== null && _d !== void 0 ? _d : []).concat((_g = (_f = (_e = resolverValue) === null || _e === void 0 ? void 0 : _e.astNode) === null || _f === void 0 ? void 0 : _f.directives) !== null && _g !== void 0 ? _g : []) });
                    }
                    else if (fieldName === 'extensionASTNodes' && config.extensionASTNodes != null) {
                        config.extensionASTNodes = config.extensionASTNodes.concat((_j = (_h = resolverValue) === null || _h === void 0 ? void 0 : _h.extensionASTNodes) !== null && _j !== void 0 ? _j : []);
                    }
                    else if (fieldName === 'extensions' &&
                        config.extensions != null &&
                        resolverValue.extensions != null) {
                        config.extensions = Object.assign({}, type.extensions, resolverValue.extensions);
                    }
                    else if (enumValueConfigMap[fieldName]) {
                        enumValueConfigMap[fieldName].value = resolverValue[fieldName];
                    }
                });
                return new GraphQLEnumType(config);
            }
        },
        _a[MapperKind.UNION_TYPE] = function (type) {
            var resolverValue = resolvers[type.name];
            if (resolverValue != null) {
                var config_2 = type.toConfig();
                Object.keys(resolverValue).forEach(function (fieldName) {
                    if (fieldName.startsWith('__')) {
                        config_2[fieldName.substring(2)] = resolverValue[fieldName];
                    }
                });
                return new GraphQLUnionType(config_2);
            }
        },
        _a[MapperKind.OBJECT_TYPE] = function (type) {
            var resolverValue = resolvers[type.name];
            if (resolverValue != null) {
                var config_3 = type.toConfig();
                Object.keys(resolverValue).forEach(function (fieldName) {
                    if (fieldName.startsWith('__')) {
                        config_3[fieldName.substring(2)] = resolverValue[fieldName];
                    }
                });
                return new GraphQLObjectType(config_3);
            }
        },
        _a[MapperKind.INTERFACE_TYPE] = function (type) {
            var resolverValue = resolvers[type.name];
            if (resolverValue != null) {
                var config_4 = type.toConfig();
                Object.keys(resolverValue).forEach(function (fieldName) {
                    if (fieldName.startsWith('__')) {
                        config_4[fieldName.substring(2)] = resolverValue[fieldName];
                    }
                });
                return new GraphQLInterfaceType(config_4);
            }
        },
        _a[MapperKind.COMPOSITE_FIELD] = function (fieldConfig, fieldName, typeName) {
            var resolverValue = resolvers[typeName];
            if (resolverValue != null) {
                var fieldResolve = resolverValue[fieldName];
                if (fieldResolve != null) {
                    var newFieldConfig = __assign({}, fieldConfig);
                    if (typeof fieldResolve === 'function') {
                        // for convenience. Allows shorter syntax in resolver definition file
                        newFieldConfig.resolve = fieldResolve;
                    }
                    else {
                        setFieldProperties(newFieldConfig, fieldResolve);
                    }
                    return newFieldConfig;
                }
            }
        },
        _a));
    if (defaultFieldResolver != null) {
        schema = mapSchema(schema, (_b = {},
            _b[MapperKind.OBJECT_FIELD] = function (fieldConfig) { return (__assign(__assign({}, fieldConfig), { resolve: fieldConfig.resolve != null ? fieldConfig.resolve : defaultFieldResolver })); },
            _b));
    }
    return schema;
}
function setFieldProperties(field, propertiesObj) {
    Object.keys(propertiesObj).forEach(function (propertyName) {
        field[propertyName] = propertiesObj[propertyName];
    });
}
//# sourceMappingURL=addResolversToSchema.js.map